/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment, useState } from 'react'
import moment from 'moment'

import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiDatePicker,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiForm,
  EuiFormRow,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { addToast } from '@modules/cui/Toasts'
import useRecalculateLedger from '@modules/billing-lib/mutations/useRecalculateLedger'

import type { Moment } from 'moment'

interface Props {
  organizationId: string
  onClose: () => void
}

interface ErrorResponseBody {
  message: string
}

const RecalculateFlyout = ({ organizationId, onClose }: Props) => {
  const [from, setFrom] = useState<Moment | null>(moment())
  const recalculateLedgerV1Mutation = useRecalculateLedger()
  const flyoutTitleId = useGeneratedHtmlId({
    prefix: 'RecalculateFlyout',
  })

  const onSubmit = () => {
    if (!from) {
      return
    }

    recalculateLedgerV1Mutation.mutate(
      {
        pathParameters: { organization_id: organizationId },
        body: { from: from.startOf('day').utc().format() },
      },
      {
        onSuccess: () => {
          addToast({
            color: 'success',
            title: (
              <FormattedMessage
                id='recalculate-flyout.success'
                defaultMessage='Successfully deleted ledger history for organization'
              />
            ),
          })
          onClose()
        },
      },
    )
  }

  const renderFailedCallOut = () => {
    const error = recalculateLedgerV1Mutation.error as ErrorResponseBody | string | undefined
    return (
      <Fragment>
        <EuiSpacer size='m' />

        <EuiCallOut
          title={
            <FormattedMessage id='recalculate-flyout.error' defaultMessage='The procedure failed' />
          }
          color='danger'
          iconType='error'
          data-test-id='fail-callout'
        >
          {typeof error === 'string' ? error : error?.message || ''}
        </EuiCallOut>
      </Fragment>
    )
  }

  return (
    <EuiFlyout
      size='m'
      maxWidth={0}
      ownFocus={true}
      onClose={onClose}
      aria-labelledby={flyoutTitleId}
    >
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2>
            <FormattedMessage id='recalculate-flyout.title' defaultMessage='Recalculate usage' />
          </h2>
        </EuiTitle>
      </EuiFlyoutHeader>

      <EuiFlyoutBody>
        <EuiText>
          <FormattedMessage
            id='recalculate-flyout.description'
            defaultMessage='Deletes ledger documents from given date to current date.'
          />
        </EuiText>

        <EuiSpacer />

        <EuiForm>
          <EuiFormRow
            label={<FormattedMessage id='recalculate-flyout.date' defaultMessage='Date' />}
            helpText={
              <FormattedMessage
                id='recalculate-flyout.date.help-text'
                defaultMessage='Date time to start the recalculation from'
              />
            }
            data-test-id='from-date-picker'
          >
            <EuiDatePicker
              dateFormat='YYYY-MM-DD'
              isInvalid={false}
              selected={from}
              maxDate={moment()}
              onChange={setFrom}
              onClear={() => setFrom(null)}
            />
          </EuiFormRow>
        </EuiForm>

        {recalculateLedgerV1Mutation.isError && renderFailedCallOut()}
      </EuiFlyoutBody>

      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='spaceBetween'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty
              iconType='cross'
              onClick={onClose}
              flush='left'
              data-test-id='close-flyout-button'
            >
              <FormattedMessage id='recalculate-flyout.cancel' defaultMessage='Cancel' />
            </EuiButtonEmpty>
          </EuiFlexItem>

          <EuiFlexItem grow={false}>
            <EuiButton
              data-test-id='recalculate-button'
              onClick={onSubmit}
              fill={true}
              isLoading={recalculateLedgerV1Mutation.isLoading}
              disabled={!from}
            >
              <FormattedMessage
                id='recalculate-flyout.confirm'
                defaultMessage='Recalculate usage'
              />
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default RecalculateFlyout
